import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { type FoundContact, loadOutreachState, type OutreachEnrollment, type OutreachState, watchOutreach } from '@/lib/outreach';
import { SENDABLE_EMAIL_STATUSES } from '@shared/outreach';

const LIVE = new Set(['drafting', 'review', 'active']);

/**
 * The campaigns page's data, live: the app's campaigns, the contacts, the
 * prospects, the background work in progress. With a campaign open, its own
 * prospects and sequences. Gestures reload when they return; the realtime
 * channel brings what the worker does.
 */
export const useOutreach = (toolAppId: string, campaignId: string | null = null) => {
  const [state, setState] = useState<OutreachState | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    const { data } = await supabase.auth.getUser();
    setUserId(data.user?.id ?? null);
    try {
      setState(await loadOutreachState(toolAppId));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Les campagnes n’ont pas pu être chargées.');
    }
  }, [toolAppId]);

  useEffect(() => {
    void reload();
    return watchOutreach(toolAppId, () => { void reload(); });
  }, [toolAppId, reload]);

  const derived = useMemo(() => {
    const contacts = state?.contacts ?? [];
    const campaign = state?.campaigns.find((item) => item.id === campaignId) ?? null;
    const isOwner = Boolean(campaign && userId && campaign.ownerUserId === userId);
    const contactsOf = (recordId: string): FoundContact[] => contacts.filter((contact) => contact.appRecordId === recordId && contact.state !== 'dismissed');
    /** The person a sequence goes to: the chosen ones, else the best ranked. */
    const primaryOf = (recordId: string): FoundContact | null => {
      const own = contactsOf(recordId);
      return own.find((contact) => contact.state === 'chosen') ?? own[0] ?? null;
    };
    /** The open campaign's sequences, whoever looks. */
    const inCampaign = (state?.enrollments ?? []).filter((enrollment) => enrollment.sequenceId === campaignId);
    const enrollmentOf = (recordId: string): OutreachEnrollment | null => inCampaign.find((enrollment) => enrollment.appRecordId === recordId) ?? null;
    /** A person is in one live sequence at a time: the other campaign holding them, if any. */
    const liveElsewhere = (contactId: string): string | null => {
      const live = (state?.enrollments ?? []).find((enrollment) => enrollment.foundContactId === contactId && enrollment.sequenceId !== campaignId && LIVE.has(enrollment.status));
      return live ? state?.campaigns.find((item) => item.id === live.sequenceId)?.name ?? null : null;
    };
    const busy = (kind: string, subjectId: string) => (state?.jobs ?? []).some((job) => job.kind === kind && job.subjectId === subjectId && job.status !== 'failed');
    const failed = (kind: string, subjectId: string) => (state?.jobs ?? []).find((job) => job.kind === kind && job.subjectId === subjectId && job.status === 'failed') ?? null;
    const sendable = (contact: FoundContact | null) => Boolean(contact?.email && contact.emailStatus && SENDABLE_EMAIL_STATUSES.has(contact.emailStatus));
    return { campaign, isOwner, contactsOf, primaryOf, enrollmentOf, inCampaign, liveElsewhere, busy, failed, sendable };
  }, [state, userId, campaignId]);

  return { state, userId, error, setError, reload, ...derived };
};

export type OutreachData = ReturnType<typeof useOutreach>;
